import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { IDocumentsFormValues } from 'models/cases';
import { FileDropzoneContainer } from 'components/molecules/FormInputFile';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { EMIMETypes } from 'constants/MIMETypes';
import { useCase } from 'pages/CaseManagementPage/CasesPage/hooks';
import { Elabels } from 'pages/CaseManagementPage/CasesPage/helpers';
import { Box, Grid, Typography } from '@mui/material';
import { FormModal } from 'components/molecules/Modal';
import { useAlert } from 'models/alertContext';
import { Alert } from 'components/atoms/Alert';
import { ActionTypes } from 'state/actions/alert';
import { useLang } from 'models/langContext';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';

interface IAttachmentUploadDocumentsForm {
  caseId: string;
  uploadDocumentId?: string | null;
  documentName?: string;
  onClose: () => void;
  onReload: () => void;
  isNewType?: boolean;
}

const AttachmentUploadDocumentsForm = ({
  caseId,
  uploadDocumentId = null,
  documentName,
  onClose,
  onReload,
  isNewType = false,
}: IAttachmentUploadDocumentsForm) => {
  const { t } = useTranslations();
  const [isLoading, setIsLoading] = useState(false);
  const { handleAddAttachments, handleAddNewTypeAttachments } = useCase();
  const { clearAlert, modalAlert } = useAlert();
  const { selectedLanguage } = useLang();
  const { loadDictionaries, attachmentDocumentTypeData } = useConfigurationLicence();

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.attachmentDocumentTypeData]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
    return () => clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
  }, []);

  const initialDocumentFormValues: IDocumentsFormValues = {
    caseUniqueId: caseId,
    documentRequirementUniqueId: uploadDocumentId ?? '',
    documentTypeUniqueId: '',
    fileNames: [],
    documents: null,
  };

  const schema = yup.object({
    caseUniqueId: yup.string().required(t('general.field.validation.required')).trim(),
    documentRequirementUniqueId: yup
      .string()
      .test('documents-required', t('general.field.validation.required'), (value) => {
        if (isNewType) return true;
        return !!value;
      })
      .trim(),
    documentTypeUniqueId: yup
      .string()
      .test('documents-required', t('general.field.validation.required'), (value) => {
        if (!isNewType) return true;
        return !!value;
      })
      .trim(),
    documents: yup
      .array()
      .min(1, t('general.field.validation.required'))
      .required(t('general.field.validation.required')),
  });
  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<IDocumentsFormValues>({
    defaultValues: initialDocumentFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IDocumentsFormValues>,
  });

  const submitForm = async (data: any) => {
    setIsLoading(true);
    let res = false;
    if (isNewType) {
      res = await handleAddNewTypeAttachments(data);
    } else {
      res = await handleAddAttachments(data);
    }
    if (res) {
      onReload();
      clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
      onClose();
    }
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <FormModal
        open={!!uploadDocumentId || isNewType}
        onClickClose={onClose}
        onClickApprove={handleSubmit(submitForm)}
        title={isNewType ? t(Elabels.addDocument) : t(Elabels.uploadDocument)}
        titleLeft
      >
        <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {modalAlert && (
            <Grid size={{ xs: 12 }}>
              <Box>
                <Alert text={modalAlert.text} variant={modalAlert.variant} />
              </Box>
            </Grid>
          )}
          {!isNewType && (
            <Grid size={{ xs: 12 }} container>
              <Typography variant="body2">
                {t(Elabels.documentName)}: <b>{documentName}</b>
              </Typography>
            </Grid>
          )}
          {isNewType && (
            <Grid size={{ xs: 12 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(attachmentDocumentTypeData)}
                setValue={setValue}
                name="documentTypeUniqueId"
                label={t(Elabels.documentName)}
                control={control}
                errors={errors}
                required
              />
            </Grid>
          )}
          <Grid sx={{ mb: 4 }}>
            <FileDropzoneContainer
              label={t(Elabels.attachment)}
              name="documents"
              fileName="fileNames"
              required
              multiple
              errors={errors}
              disabled={isLoading}
              setValue={setValue}
              disabledInputName={false}
              allowedType={[EMIMETypes.JPEG, EMIMETypes.JPG, EMIMETypes.PNG, EMIMETypes.PDF]}
            />
          </Grid>
        </Grid>
      </FormModal>
    </form>
  );
};

export default AttachmentUploadDocumentsForm;
